import { useState, useEffect, useMemo, type CSSProperties } from 'react';
import {
  collection, doc, getDoc, runTransaction, serverTimestamp,
} from 'firebase/firestore';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../hooks/useSettings';
import { Button, Modal, ModalBanner, FormField } from './ui';
import { fmtMoney } from '../utils/currency';
import { buildEntry, postEntry, entryId, linesSaleReturn } from '../utils/ledger';
import { returnGst, returnCreditOf } from '../utils/tradingSystem';
import type { SaleInvoice, InventoryItem, PaymentStatus } from '../types';

function todayISO() { return new Date().toISOString().slice(0, 10); }
function stripUndef<T extends object>(obj: T): T {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;
}
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

// In-table input — mirrors Purchase's line-item `TI` so a table cell's input
// isn't a raw browser default next to the rest of the app's form styling.
// Sales invoice No shown as prefix + last 4 digits — full value stays in the saved record.
function lotNoDisplay(invoiceNo: string) {
  if (!invoiceNo) return '—';
  const m = invoiceNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : invoiceNo.slice(-4);
}
const GTD: CSSProperties = { padding: 0, verticalAlign: 'middle' };
const GTD_TEXT: CSSProperties = { ...GTD, padding: '6px 8px' };
const GTH: CSSProperties = {
  position: 'sticky', top: 0, zIndex: 2, background: 'var(--surface-2)',
  padding: '6px 8px', fontSize: 9.5, fontWeight: 700, color: 'var(--text-3)',
  textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left',
  whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)',
};
const codePillStyle: CSSProperties = {
  fontFamily: 'var(--font-num)', background: 'var(--primary-light)', color: 'var(--primary)',
  padding: '3px 8px', borderRadius: 6, marginRight: 6, fontSize: 12, fontWeight: 700,
  letterSpacing: '0.02em',
};
function normalizeCode(code: string): string {
  return code.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '_').slice(0, 400);
}

// price undefined = not typed yet (the box shows what the customer paid).
interface LineState { returnCarat: string; reason: string; price?: string }
// Every cell in this table reads at one size, inputs included (grid-cell is 11px).
const CELL: CSSProperties = { fontSize: 12 };
/** What the customer was charged per ct on this line (after Keep %). */
const soldPerCt = (li: { amountUSD?: number; carat: number; salePricePerCt?: number }) =>
  li.carat > 0 ? (li.amountUSD ?? 0) / li.carat : (li.salePricePerCt ?? 0);

export default function SalesReturnModal({ sale, customerName, onClose }: {
  sale: SaleInvoice;
  customerName: string;
  onClose: () => void;
}) {
  const { user, business } = useAuth();
  const settings = useSettings();
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  // Current inventory state of every sold doc/slice the sale points at.
  const [invById, setInvById] = useState<Record<string, InventoryItem> | null>(null);
  const [date, setDate]   = useState(todayISO());
  const [notes, setNotes] = useState('');
  const [ls, setLs]       = useState<Record<number, LineState>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const ids = [...new Set(sale.lineItems.map(li => li.inventoryItemId))];
    Promise.all(ids.map(id => getDoc(doc(db, 'inventory', id))))
      .then(async snaps => {
        const m: Record<string, InventoryItem> = {};
        snaps.forEach(s => { if (s.exists()) m[s.id] = { id: s.id, ...s.data() } as InventoryItem; });
        // The parcels the sold pieces were cut from: a return goes back into them.
        const parentIds = [...new Set(Object.values(m).map(i => i.splitFromId).filter((x): x is string => !!x && !m[x]))];
        const ps = await Promise.all(parentIds.map(id => getDoc(doc(db, 'inventory', id))));
        ps.forEach(s => { if (s.exists()) m[s.id] = { id: s.id, ...s.data() } as InventoryItem; });
        setInvById(m);
      })
      .catch(() => setInvById({}));
  }, [sale.id, sale.lineItems]);

  const set = (i: number, patch: Partial<LineState>) =>
    setLs(s => ({ ...s, [i]: { ...(s[i] ?? { returnCarat: '', reason: '' }), ...patch } }));

  // The in-stock parcel (same Code) a sold piece goes back into, if any.
  const parcelOf = (cur: InventoryItem): InventoryItem | undefined => {
    const p = cur.splitFromId && cur.splitFromId !== settings.dummyInventoryItemId ? invById?.[cur.splitFromId] : undefined;
    return p && p.status === 'in-stock' && (p.code ?? '') === (cur.code ?? '') && (p.carat ?? 0) > 1e-9 ? p : undefined;
  };

  // A sale line is returnable only while its inventory doc is still 'sold'.
  const returnable = useMemo(() => {
    if (!invById) return [];
    return sale.lineItems
      .map((li, i) => {
        const cur = invById[li.inventoryItemId];
        if (!cur || cur.status !== 'sold' || (cur.carat ?? 0) <= 1e-6) return null;
        // A Books Adjustment (Dummy code) line isn't physical stock — there's
        // nothing to restock. Undo it by editing/deleting the sale instead,
        // which puts its carats and amount back on the Dummy balance.
        if (settings.dummyInventoryItemId && cur.splitFromId === settings.dummyInventoryItemId) return null;
        return { li, i, cur };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [invById, sale.lineItems, settings.dummyInventoryItemId]);

  const planned = useMemo(() => {
    return returnable
      .map(({ li, i, cur }) => {
        const rc = parseFloat(ls[i]?.returnCarat ?? '');
        if (isNaN(rc) || rc <= 0) return null;
        const carat = Math.min(rc, cur.carat);
        // Price/ct to credit: starts at what the customer paid, can be lowered
        // (e.g. the stone came back damaged, so less is credited).
        const typed = parseFloat(ls[i]?.price ?? '');
        const price = isNaN(typed) ? soldPerCt(li) : typed;
        const revenue = round2(carat * price);
        const cost    = round2((cur.costUSD ?? 0) * (carat / cur.carat));
        return { li, i, cur, carat, price, revenue, cost, reason: ls[i]?.reason?.trim() || undefined };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [returnable, ls]);

  const totalCarat   = round2(planned.reduce((s, p) => s + p.carat, 0));
  const totalRevenue = round2(planned.reduce((s, p) => s + p.revenue, 0));
  // Indian GST on the credited goods, at the sale's own rates.
  const totalGst     = returnGst(sale, totalRevenue)?.taxTotalUSD ?? 0;

  async function save() {
    if (planned.length === 0) return setError('Enter a carat to return on at least one line.');
    const badPrice = planned.find(p => p.price < 0 || p.price > soldPerCt(p.li) + 0.005);
    if (badPrice) {
      return setError(`${badPrice.cur.code || badPrice.li.shape}: Price/ct must be between 0 and ${money(soldPerCt(badPrice.li))} (what the customer paid).`);
    }
    setSaving(true); setError('');
    const db = getClientDb();
    if (!db || !business) { setError('Not connected.'); setSaving(false); return; }

    const prefix = (settings.creditNotePrefix || 'CN').toUpperCase();
    const retRef = doc(collection(db, 'salesReturns'));
    const saleRef = doc(db, 'sales', sale.id);
    const counterRef = doc(db, 'counters', business.id);
    // Pre-allocate slice refs for partial returns.
    const sliceRefs = planned.map(() => doc(collection(db, 'inventory')));

    try {
      await runTransaction(db, async tx => {
        const counterSnap = await tx.get(counterRef);
        const fresh = await Promise.all(planned.map(p => tx.get(doc(db, 'inventory', p.cur.id ?? p.li.inventoryItemId))));
        const saleSnap = await tx.get(saleRef);
        // The parcel each sold piece was cut from (partial sales). When it's
        // still in stock, the returned carats go back into it, not a new row.
        const parentIds = [...new Set(fresh.map(s => (s.exists() ? (s.data() as InventoryItem).splitFromId : undefined))
          .filter((x): x is string => !!x && x !== settings.dummyInventoryItemId))];
        const parentSnaps = await Promise.all(parentIds.map(id => tx.get(doc(db, 'inventory', id))));
        const parents = new Map<string, InventoryItem>();
        parentSnaps.forEach(ps => { if (ps.exists()) parents.set(ps.id, { id: ps.id, ...ps.data() } as InventoryItem); });
        if (!saleSnap.exists()) throw new Error('That sale no longer exists.');

        const n = (counterSnap.exists() ? (counterSnap.data().salesReturn ?? 0) : 0) + 1;
        const rows = planned.map((p, idx) => {
          const s = fresh[idx];
          if (!s.exists()) throw new Error(`${p.li.shape} is no longer in inventory.`);
          const cur = { id: s.id, ...s.data() } as InventoryItem;
          if (cur.status !== 'sold') throw new Error(`${p.li.shape} is ${cur.status} — it can't be returned.`);
          if (p.carat > cur.carat + 1e-6) throw new Error(`Only ${cur.carat.toFixed(2)}ct of ${p.li.shape} can come back.`);
          const cost    = round2((cur.costUSD ?? 0) * (p.carat / cur.carat));
          const costing = round2((cur.costingUSD ?? 0) * (p.carat / cur.carat));
          const revenue = round2(p.carat * p.price);
          const wholeItem = round2(cur.carat - p.carat) <= 1e-6;
          const parent = cur.splitFromId ? parents.get(cur.splitFromId) : undefined;
          const intoParent = parent && parent.status === 'in-stock' && (parent.code ?? '') === (cur.code ?? '') && (parent.carat ?? 0) > 1e-9 ? parent : undefined;
          // Back into a parcel: valued at the parcel's CURRENT Cost/ct and
          // Price/ct (so its per ct figures don't move), not at the old cost
          // the sold piece carried. The sold piece still gives up its own cost.
          const stockCost    = intoParent ? round2(p.carat * (intoParent.costUSD ?? 0) / intoParent.carat) : cost;
          const stockCosting = intoParent ? round2(p.carat * (intoParent.costingUSD ?? 0) / intoParent.carat) : costing;
          return { p, cur, cost: stockCost, costing: stockCosting, pieceCost: cost, pieceCosting: costing, revenue, wholeItem, sliceRef: sliceRefs[idx], intoParent };
        });
        // Running carat / cost per parent, in case two lines go back into the same one.
        const parentAdd = new Map<string, { carat: number; cost: number; costing: number }>();

        const totRev  = round2(rows.reduce((s, r) => s + r.revenue, 0));
        const totCost = round2(rows.reduce((s, r) => s + r.cost, 0));
        const totCt   = round2(rows.reduce((s, r) => s + r.p.carat, 0));

        const sale0 = saleSnap.data() as SaleInvoice;
        // The customer is credited the GST on these goods too (the sale
        // total includes it), at the rates the sale charged.
        const gstF = returnGst(sale0, totRev);
        const credit = returnCreditOf(totRev, gstF ?? {});
        const newCredited = round2((sale0.creditedUSD ?? 0) + credit);
        const settled = (sale0.paidUSD ?? 0) + newCredited;
        const newStatus: PaymentStatus =
          settled >= sale0.totalAmountUSD - 0.005 ? 'paid' : settled > 0 ? 'partial' : 'unpaid';

        // ── Writes ──
        tx.set(counterRef, { salesReturn: n }, { merge: true });
        tx.set(retRef, stripUndef({
          businessId: business.id,
          returnNo: `${prefix}-${new Date().getFullYear()}-${String(n).padStart(4, '0')}`,
          saleId: sale.id, saleInvoiceNo: sale.invoiceNo,
          toEntityId: sale.toEntityId, saleDate: sale.saleDate, date,
          lines: rows.map(r => stripUndef({
            inventoryItemId: r.cur.id, code: r.cur.code || undefined, shape: r.cur.shape,
            carat: r.p.carat, pricePerCt: r.p.price, revenueUSD: r.revenue, costUSD: r.cost, costingUSD: r.costing,
            wholeItem: r.wholeItem,
            sliceId: r.intoParent || r.wholeItem ? undefined : r.sliceRef.id,
            mergedIntoId: r.intoParent?.id,
            soldAmountBefore: r.intoParent ? (r.cur.soldAmountUSD ?? undefined) : undefined,
            pieceCostUSD: r.intoParent ? r.pieceCost : undefined,
            pieceCostingUSD: r.intoParent ? r.pieceCosting : undefined,
            reason: r.p.reason,
          })),
          totalCarat: totCt, totalRevenueUSD: totRev, totalCostUSD: totCost,
          ...(gstF ?? {}),
          notes: notes.trim() || undefined,
          createdAt: serverTimestamp(), createdBy: user!.id,
        }));

        rows.forEach(r => {
          const itRef = doc(db, 'inventory', r.cur.id);
          if (r.intoParent) {
            // Back into the original parcel: it gains the carats and their
            // cost; the sold piece shrinks (or empties and leaves the list).
            const acc = parentAdd.get(r.intoParent.id) ?? { carat: 0, cost: 0, costing: 0 };
            acc.carat += r.p.carat; acc.cost += r.cost; acc.costing += r.costing;
            parentAdd.set(r.intoParent.id, acc);
            const newCarat = round2(r.cur.carat - r.p.carat);
            tx.update(itRef, stripUndef({
              carat: r.wholeItem ? 0 : newCarat,
              costUSD: r.wholeItem ? 0 : round2((r.cur.costUSD ?? 0) - r.pieceCost),
              costingUSD: r.wholeItem ? 0 : round2((r.cur.costingUSD ?? 0) - r.pieceCosting),
              soldAmountUSD: r.wholeItem ? 0 : round2((r.cur.soldAmountUSD ?? 0) * (newCarat / r.cur.carat)),
              ...(r.wholeItem ? { status: 'merged' } : {}),
              updatedAt: serverTimestamp(),
            }));
          } else if (r.wholeItem) {
            tx.update(itRef, stripUndef({
              status: 'in-stock',
              soldToEntityId: null, soldDate: null, soldAmountUSD: null,
              updatedAt: serverTimestamp(),
            }));
          } else {
            const newCarat = round2(r.cur.carat - r.p.carat);
            tx.set(r.sliceRef, stripUndef({
              businessId: business.id,
              purchaseId: r.cur.purchaseId, purchaseNo: r.cur.purchaseNo, lineItemId: r.cur.lineItemId,
              type: r.cur.type, status: 'in-stock',
              shape: r.cur.shape, carat: r.p.carat, color: r.cur.color, clarity: r.cur.clarity,
              size: r.cur.size, subSize: r.cur.subSize, sku: r.cur.sku, skuId: r.cur.skuId,
              code: r.cur.code || undefined,
              costUSD: r.cost, costingUSD: r.costing,
              splitFromId: r.cur.splitFromId ?? r.cur.id,
              createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
            }));
            tx.update(itRef, stripUndef({
              carat: newCarat,
              costUSD: round2((r.cur.costUSD ?? 0) - r.cost),
              costingUSD: round2((r.cur.costingUSD ?? 0) - r.costing),
              soldAmountUSD: round2((r.cur.soldAmountUSD ?? 0) * (newCarat / r.cur.carat)),
              updatedAt: serverTimestamp(),
            }));
          }
          if (r.cur.code) {
            tx.set(doc(collection(db, 'itemCodes', normalizeCode(r.cur.code), 'history')), stripUndef({
              type: 'return-in', date, carat: r.p.carat,
              entityId: sale.toEntityId, pricePerCt: r.p.price, amountUSD: r.revenue,
              inventoryItemId: r.intoParent ? r.intoParent.id : r.wholeItem ? r.cur.id : r.sliceRef.id,
              notes: `Returned by customer (${sale.invoiceNo})`,
              returnId: retRef.id,   // so deleting the credit note removes this line too
              createdAt: serverTimestamp(), createdBy: user!.id,
            }));
          }
        });

        parentAdd.forEach((add, id) => {
          const par = parents.get(id)!;
          tx.update(doc(db, 'inventory', id), {
            carat: round2((par.carat ?? 0) + add.carat),
            costUSD: round2((par.costUSD ?? 0) + add.cost),
            costingUSD: round2((par.costingUSD ?? 0) + add.costing),
            updatedAt: serverTimestamp(),
          });
        });

        tx.update(saleRef, { creditedUSD: newCredited, paymentStatus: newStatus, updatedAt: serverTimestamp() });

        postEntry(tx, db, buildEntry({
          businessId: business.id, sourceType: 'sales_return', sourceId: retRef.id,
          sourceCollection: 'salesReturns', date, currency: settings.currency,
          createdBy: user!.id, note: `Credit note against ${sale.invoiceNo}`,
          lines: linesSaleReturn(totRev, totCost, sale.toEntityId, gstF?.taxTotalUSD ?? 0),
        }), entryId('sales_return', retRef.id));
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record the return.');
    }
    setSaving(false);
  }

  return (
    <Modal
      open onClose={onClose} size="2xl" hideHead
      footer={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
            Crediting <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{money(round2(totalRevenue + totalGst))}</strong>
            {totalGst > 0 && <> (incl. GST {money(totalGst)})</>}
            {' · '}restocking <strong style={{ color: 'var(--text-1)', fontFamily: 'var(--font-num)' }}>{totalCarat.toFixed(2)} ct</strong>
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={save} loading={saving} disabled={planned.length === 0}>Save</Button>
          </div>
        </div>
      }
    >
      <ModalBanner title={customerName} eyebrow={`Sales Return / Credit Note · ${lotNoDisplay(sale.invoiceNo)}`} onClose={onClose} />

      {error && <div className="error-msg" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="form-row-2" style={{ marginBottom: 14 }}>
        <FormField label="Date">
          <input type="date" value={date} max={todayISO()} onChange={e => setDate(e.target.value)} />
        </FormField>
        <FormField label="Notes (optional)">
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Why is it coming back?" />
        </FormField>
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
        Items coming back
      </div>

      {invById === null ? (
        <div className="skeleton" style={{ height: 160, borderRadius: 8 }} />
      ) : returnable.length === 0 ? (
        <div style={{ padding: '28px 16px', textAlign: 'center', fontSize: 13, color: 'var(--text-3)', border: '1px dashed var(--border)', borderRadius: 'var(--r-sm)' }}>
          Nothing on this sale can be returned — the stones have been re-sold, merged, or already returned.
        </div>
      ) : (
        <div style={{
          border: '1.5px solid var(--border)', borderRadius: 'var(--r-sm)',
          overflow: 'auto', maxHeight: 360, overscrollBehavior: 'contain',
        }}>
          <table className="excel-grid-table">
            <thead>
              <tr>
                <th style={{ ...GTH, minWidth: 90 }}>Code</th>
                <th style={{ ...GTH, minWidth: 100 }}>SKU</th>
                <th style={{ ...GTH, minWidth: 90 }}>Shape</th>
                <th style={{ ...GTH, minWidth: 70 }}>Color</th>
                <th style={{ ...GTH, minWidth: 80 }}>Clarity</th>
                <th style={{ ...GTH, textAlign: 'right', width: 110 }}>Sold ct</th>
                <th style={{ ...GTH, width: 130 }}>Return ct</th>
                <th style={{ ...GTH, textAlign: 'right', width: 120 }}>Price/ct</th>
                <th style={{ ...GTH, textAlign: 'right', width: 130 }}>Credit</th>
                <th style={{ ...GTH, textAlign: 'right', width: 120 }} title="The Price/ct the stones go back into stock at: the parcel's current figure when they rejoin it">Back at /ct</th>
                <th style={{ ...GTH, minWidth: 200 }}>Return Reason</th>
              </tr>
            </thead>
            <tbody>
              {returnable.map(({ li, i, cur }) => {
                const rc = parseFloat(ls[i]?.returnCarat ?? '');
                const paid = soldPerCt(li);
                const typed = parseFloat(ls[i]?.price ?? '');
                const price = isNaN(typed) ? paid : typed;
                const priceBad = price < 0 || price > paid + 0.005;
                const credit = !isNaN(rc) && rc > 0 ? round2(Math.min(rc, cur.carat) * price) : 0;
                return (
                  <tr key={i}>
                    <td style={{ ...GTD_TEXT, ...CELL }}>
                      {cur.code ? <span style={codePillStyle}>{cur.code}</span> : <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                    <td style={{ ...GTD_TEXT, fontSize: 12, fontFamily: 'var(--font-num)', color: 'var(--text-2)' }}>{cur.sku || <span style={{ opacity: 0.4 }}>—</span>}</td>
                    <td style={{ ...GTD_TEXT, fontSize: 12, color: 'var(--text-2)' }}>{cur.shape || '—'}</td>
                    <td style={{ ...GTD_TEXT, fontSize: 12, color: 'var(--text-2)' }}>{cur.color || '—'}</td>
                    <td style={{ ...GTD_TEXT, fontSize: 12, color: 'var(--text-2)' }}>{cur.clarity || '—'}</td>
                    <td style={{ ...GTD_TEXT, textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)' }}>{cur.carat.toFixed(2)}</td>
                    <td style={GTD}>
                      <input type="number" step="0.01" min="0" max={cur.carat} className="grid-cell no-spinner"
                        value={ls[i]?.returnCarat ?? ''}
                        onChange={e => set(i, { returnCarat: e.target.value })}
                        style={{ ...CELL, textAlign: 'right', fontFamily: 'var(--font-num)' }} />
                    </td>
                    <td style={{ ...GTD, background: priceBad ? 'color-mix(in srgb, var(--danger) 12%, transparent)' : undefined }}
                      title={`Customer paid ${money(paid)} per ct. Lower it to credit less.`}>
                      <input type="number" step="0.01" min="0" max={paid} className="grid-cell no-spinner"
                        value={ls[i]?.price ?? paid.toFixed(2)}
                        onChange={e => set(i, { price: e.target.value })}
                        style={{ ...CELL, textAlign: 'right', fontFamily: 'var(--font-num)' }} />
                    </td>
                    <td style={{ ...GTD_TEXT, textAlign: 'right', fontFamily: 'var(--font-num)', fontSize: 12, color: credit ? 'var(--text-1)' : 'var(--text-3)', fontWeight: credit ? 600 : 400 }}>
                      {credit ? money(credit) : '—'}
                    </td>
                    {(() => {
                      // Rejoins its parcel at the parcel's current per ct; else keeps its own.
                      const parcel = parcelOf(cur);
                      const src = parcel ?? cur;
                      const back = (src.carat ?? 0) > 0 ? (src.costingUSD ?? 0) / src.carat : 0;
                      const cpc  = (src.carat ?? 0) > 0 ? (src.costUSD ?? 0) / src.carat : 0;
                      return (
                        <td style={{ ...GTD_TEXT, ...CELL, textAlign: 'right', fontFamily: 'var(--font-num)', color: 'var(--text-2)' }}
                          title={parcel
                            ? `Goes back into parcel ${parcel.code || ''} (${(parcel.carat ?? 0).toFixed(2)} ct) at its Cost/ct ${money(cpc)}, Price/ct ${money(back)}`
                            : `Comes back as its own stock at Cost/ct ${money(cpc)}, Price/ct ${money(back)}`}>
                          {money(back)}
                          {parcel && <div style={{ fontSize: 10.5, color: 'var(--text-3)', fontFamily: 'inherit' }}>into parcel</div>}
                        </td>
                      );
                    })()}
                    <td style={GTD}>
                      <input value={ls[i]?.reason ?? ''} onChange={e => set(i, { reason: e.target.value })}
                        className="grid-cell" placeholder="optional" style={CELL} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
